import React, { useState, useMemo } from 'react';
import {
  sanitizeTemplateConfig,
  type HtmlTemplateConfig,
  type TableWidget,
  type TextWidget,
} from '../../../../services/htmlTemplatesApi';
import { Smartphone, Monitor, Code, Eye, Copy, Check } from 'lucide-react';
import toast from 'react-hot-toast';

interface HtmlEmailPreviewProps {
  config: HtmlTemplateConfig;
  title?: string;
  hideHeader?: boolean;
  borderless?: boolean;
  deviceView?: 'desktop' | 'mobile';
  onDeviceViewChange?: (view: 'desktop' | 'mobile') => void;
  showRawHtml?: boolean;
  onShowRawHtmlChange?: (show: boolean) => void;
}

export const HtmlEmailPreview: React.FC<HtmlEmailPreviewProps> = ({
  config,
  title,
  hideHeader = false,
  borderless = false,
  deviceView: deviceViewProp,
  onDeviceViewChange,
  showRawHtml: showRawHtmlProp,
  onShowRawHtmlChange,
}) => {
  const [internalDeviceView, setInternalDeviceView] = useState<'desktop' | 'mobile'>('desktop');
  const [internalShowRawHtml, setInternalShowRawHtml] = useState(false);
  const [copied, setCopied] = useState(false);

  const deviceView = deviceViewProp !== undefined ? deviceViewProp : internalDeviceView;
  const showRawHtml = showRawHtmlProp !== undefined ? showRawHtmlProp : internalShowRawHtml;

  const handleDeviceViewChange = (val: 'desktop' | 'mobile') => {
    if (onDeviceViewChange) {
      onDeviceViewChange(val);
    } else {
      setInternalDeviceView(val);
    }
  };

  const handleShowRawHtmlChange = (val: boolean) => {
    if (onShowRawHtmlChange) {
      onShowRawHtmlChange(val);
    } else {
      setInternalShowRawHtml(val);
    }
  };

  // Safely sanitize and normalize config so widgets and styles are always preserved
  const safeConfig = useMemo(() => sanitizeTemplateConfig(config), [config]);

  // Theme styling with clean white defaults
  const primaryColor = safeConfig.primary_color || '#ef4444';
  const outerBg = safeConfig.background_color || '#f8fafc';
  const cardBg = safeConfig.card_background || '#ffffff';
  const textColor = safeConfig.text_color || '#111827';
  const mutedColor = safeConfig.muted_color || '#64748b';
  const borderColor = safeConfig.border_color || '#e2e8f0';
  const maxWidth = safeConfig.max_width || '640px';
  const fontFamily = safeConfig.font_family || 'system-ui, -apple-system, sans-serif';

  const mockRows = [1, 2];

  // Generate Raw HTML for export/copy
  const generateRawHtml = () => {
    const widgetsHtml = (safeConfig.widgets || [])
      .map((w) => {
        if (w.type === 'text') {
          return `  <div style="margin-bottom: 16px; line-height: 1.6; color: ${textColor}; font-size: 14px;">
    ${(w as TextWidget).content || ''}
  </div>`;
        }
        if (w.type === 'divider') {
          return `  <hr style="border: none; border-top: 1px solid ${borderColor}; margin: 20px 0;" />`;
        }
        if (w.type === 'table') {
          const tw = w as TableWidget;
          const cols = (tw.columns || [])
            .map(
              (c) =>
                `<th style="padding: 10px 12px; text-align: ${c.align || 'left'}; width: ${
                  c.width || 'auto'
                }; border-bottom: 2px solid ${borderColor}; color: ${mutedColor}; font-size: 12px; text-transform: uppercase;">${
                  c.label
                }</th>`
            )
            .join('\n      ');
          return `  <div style="margin: 20px 0;">
    ${
      tw.title
        ? `<h4 style="margin: 0 0 10px 0; color: ${textColor}; font-size: 14px; font-weight: 600;">${tw.title}</h4>`
        : ''
    }
    <table style="width: 100%; border-collapse: collapse; font-size: 13px; color: ${textColor};">
      <thead>
        <tr>
          ${cols}
        </tr>
      </thead>
      <tbody>
        <!-- Dynamic loop: {% for row in ${tw.source || 'items'} %} -->
        <tr>
          ${(tw.columns || [])
            .map((c) => {
              const cleanField = (c.field || '').replace(/^item\./, '');
              return `<td style="padding: 10px 12px; border-bottom: 1px solid ${borderColor}; text-align: ${
                c.align || 'left'
              };">{{ ${cleanField} }}</td>`;
            })
            .join('\n          ')}
        </tr>
      </tbody>
    </table>
  </div>`;
        }
        return '';
      })
      .join('\n');

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeConfig.header || 'Notification'}</title>
</head>
<body style="margin: 0; padding: 24px; background-color: ${outerBg}; font-family: ${fontFamily}; color: ${textColor};">
  <table role="presentation" style="width: 100%; border-collapse: collapse;">
    <tr>
      <td align="center">
        <div style="max-width: ${maxWidth}; width: 100%; background-color: ${cardBg}; border: 1px solid ${borderColor}; border-radius: 12px; overflow: hidden; text-align: left; box-shadow: 0 10px 25px rgba(0,0,0,0.5);">
          <!-- Header -->
          <div style="padding: 24px; background-color: ${
            safeConfig.header_bg_color || 'transparent'
          }; border-bottom: 1px solid ${borderColor};">
            ${
              safeConfig.logo_url
                ? `<img src="${safeConfig.logo_url}" alt="Logo" style="max-width: ${
                    safeConfig.logo_width || '120px'
                  }; display: block; margin-bottom: 12px;" />`
                : ''
            }
            ${
              safeConfig.header
                ? `<h1 style="margin: 0; color: ${
                    safeConfig.header_text_color || textColor
                  }; font-size: ${safeConfig.header_font_size || '20px'}; font-weight: ${
                    safeConfig.header_font_weight || 'bold'
                  }; text-align: ${safeConfig.header_align || 'left'};">${safeConfig.header}</h1>`
                : ''
            }
          </div>
          <!-- Body Content -->
          <div style="padding: 24px;">
${widgetsHtml}
          </div>
          <!-- Footer -->
          ${
            safeConfig.footer
              ? `<div style="padding: 16px 24px; background-color: rgba(0,0,0,0.2); border-top: 1px solid ${borderColor}; color: ${mutedColor}; font-size: 11px; text-align: center;">${safeConfig.footer}</div>`
              : ''
          }
        </div>
      </td>
    </tr>
  </table>
</body>
</html>`;
  };

  const handleCopyCode = () => {
    try {
      navigator.clipboard.writeText(generateRawHtml());
      setCopied(true);
      toast.success('Raw HTML code copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy code');
    }
  };

  return (
    <div
      className={`flex flex-col h-full overflow-hidden ${
        borderless
          ? 'bg-transparent'
          : 'bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-xl dark:shadow-2xl'
      }`}
    >
      {/* Optional Preview Header & Controls */}
      {!hideHeader && (
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-900/80 backdrop-blur-md flex-none">
          <div className="flex items-center gap-2">
            {title ? (
              <>
                <Eye className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
                <span className="text-xs font-bold text-gray-900 dark:text-white tracking-wide">{title}</span>
              </>
            ) : (
              <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Preview
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            {/* Desktop / Mobile Switcher */}
            <div className="flex bg-gray-100 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-lg p-0.5">
              <button
                type="button"
                onClick={() => handleDeviceViewChange('desktop')}
                className={`p-1 rounded text-xs transition-colors cursor-pointer ${
                  deviceView === 'desktop'
                    ? 'bg-orange-500 text-white shadow'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
                title="Desktop width (Full max-width)"
              >
                <Monitor className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => handleDeviceViewChange('mobile')}
                className={`p-1 rounded text-xs transition-colors cursor-pointer ${
                  deviceView === 'mobile'
                    ? 'bg-orange-500 text-white shadow'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
                title="Mobile width (380px)"
              >
                <Smartphone className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="w-px h-3.5 bg-gray-200 dark:bg-gray-800 mx-0.5" />

            {/* Raw HTML vs Visual Preview Switcher */}
            <button
              type="button"
              onClick={() => handleShowRawHtmlChange(!showRawHtml)}
              className={`p-1.5 rounded-lg text-xs transition-colors cursor-pointer ${
                showRawHtml
                  ? 'bg-cyan-500/10 dark:bg-cyan-500/20 text-cyan-600 dark:text-cyan-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
              title={showRawHtml ? 'View Visual Preview' : 'View Raw HTML Code'}
            >
              {showRawHtml ? <Eye className="w-3.5 h-3.5" /> : <Code className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      )}

      {/* Preview Stage Container */}
      <div
        className="flex-1 overflow-y-auto custom-scroll p-4 transition-colors flex justify-center items-start"
        style={{ backgroundColor: outerBg }}
      >
        {showRawHtml ? (
          <div className="w-full h-full max-w-4xl flex flex-col space-y-2">
            <div className="flex items-center justify-between px-1">
              <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Compiled HTML Output
              </span>
              <button
                type="button"
                onClick={handleCopyCode}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-xs font-semibold shadow-sm transition-colors cursor-pointer"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" /> : <Copy className="w-3.5 h-3.5 text-cyan-500 dark:text-cyan-400" />}
                <span>{copied ? 'Copied' : 'Copy HTML'}</span>
              </button>
            </div>
            <pre className="flex-1 text-[11px] font-mono p-4 rounded-xl bg-gray-900 border border-gray-800 text-cyan-300 overflow-x-auto custom-scroll leading-relaxed select-all">
              {generateRawHtml()}
            </pre>
          </div>
        ) : (
          <div
            className="w-full rounded-2xl border transition-all duration-300 shadow-xl dark:shadow-2xl overflow-hidden"
            style={{
              maxWidth: deviceView === 'mobile' ? '380px' : maxWidth,
              backgroundColor: cardBg,
              borderColor: borderColor,
              fontFamily: fontFamily,
              color: textColor,
            }}
          >
            {/* Header Section */}
            {(safeConfig.header || safeConfig.logo_url) && (
              <div
                className="p-5 border-b transition-colors"
                style={{
                  backgroundColor: safeConfig.header_bg_color || 'transparent',
                  borderColor: borderColor,
                  textAlign: safeConfig.header_align || 'left',
                }}
              >
                {/* Logo rendering according to logo_position */}
                {safeConfig.logo_url && (
                  <div
                    className={`mb-3 ${
                      safeConfig.logo_position === 'top-center'
                        ? 'text-center'
                        : safeConfig.logo_position === 'top-right'
                        ? 'text-right'
                        : safeConfig.logo_position === 'inline-right'
                        ? 'float-right ml-4 mb-2'
                        : safeConfig.logo_position === 'inline-left'
                        ? 'float-left mr-4 mb-2'
                        : 'text-left'
                    }`}
                  >
                    <img
                      src={safeConfig.logo_url}
                      alt="Logo"
                      style={{
                        maxWidth: safeConfig.logo_width || '120px',
                        display: safeConfig.logo_position?.startsWith('inline')
                          ? 'inline-block'
                          : 'inline-block',
                      }}
                      className="rounded"
                      onError={(e) => {
                        (e.target as HTMLElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}

                {safeConfig.header && (
                  <h2
                    style={{
                      color: safeConfig.header_text_color || textColor,
                      fontSize: safeConfig.header_font_size || '20px',
                      fontWeight: safeConfig.header_font_weight || '700',
                    }}
                    className="leading-snug tracking-tight m-0"
                  >
                    {safeConfig.header}
                  </h2>
                )}

                <div className="clear-both" />
              </div>
            )}

            {/* Widgets Body Section */}
            <div className="p-5 space-y-4">
              {(!safeConfig.widgets || safeConfig.widgets.length === 0) && (
                <div
                  className="py-10 text-center text-xs italic border border-dashed rounded-xl"
                  style={{ borderColor: borderColor, color: mutedColor }}
                >
                  No widgets configured yet. Add text, table, or divider widgets in the editor.
                </div>
              )}

              {(safeConfig.widgets || []).map((widget, idx) => {
                if (widget.type === 'text') {
                  const tw = widget as TextWidget;
                  return (
                    <div
                      key={tw.id || idx}
                      className="text-xs leading-relaxed rich-text-preview"
                      style={{ color: textColor }}
                      dangerouslySetInnerHTML={{
                        __html: tw.content || '',
                      }}
                    />
                  );
                }

                if (widget.type === 'divider') {
                  return (
                    <div
                      key={widget.id || idx}
                      className="my-3 border-t"
                      style={{ borderColor: borderColor }}
                    />
                  );
                }

                if (widget.type === 'table') {
                  const tbl = widget as TableWidget;
                  return (
                    <div key={tbl.id || idx} className="space-y-2 my-3">
                      {tbl.title && (
                        <h4
                          className="text-xs font-bold tracking-wide uppercase"
                          style={{ color: primaryColor }}
                        >
                          {tbl.title}
                        </h4>
                      )}

                      <div
                        className="rounded-xl border overflow-x-auto custom-scroll"
                        style={{ borderColor: borderColor }}
                      >
                        <table className="w-full text-left border-collapse text-[11.5px]">
                          <thead>
                            <tr
                              style={{
                                backgroundColor: 'rgba(128,128,128,0.06)',
                                borderBottom: `1px solid ${borderColor}`,
                              }}
                            >
                              {(tbl.columns || []).map((col, cIdx) => (
                                <th
                                  key={cIdx}
                                  className="px-3 py-2 font-bold uppercase tracking-wider text-[10px]"
                                  style={{
                                    textAlign: col.align || 'left',
                                    width: col.width || 'auto',
                                    color: mutedColor,
                                  }}
                                >
                                  {col.label}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {!tbl.columns || tbl.columns.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={4}
                                  className="p-3 text-center italic text-xs"
                                  style={{ color: mutedColor }}
                                >
                                  No columns configured for this table.
                                </td>
                              </tr>
                            ) : (
                              mockRows.map((_, rIdx) => (
                                <tr
                                  key={rIdx}
                                  style={{
                                    borderBottom:
                                      rIdx !== mockRows.length - 1
                                        ? `1px solid ${borderColor}`
                                        : 'none',
                                    backgroundColor:
                                      rIdx % 2 === 1 ? 'rgba(128,128,128,0.03)' : 'transparent',
                                  }}
                                >
                                  {tbl.columns.map((col, cIdx) => {
                                    const cleanField = (col.field || '').replace(/^item\./, '');
                                    const val = `{{ ${cleanField} }}`;
                                    return (
                                      <td
                                        key={cIdx}
                                        className="px-3 py-2.5 font-mono text-[11px]"
                                        style={{
                                          textAlign: col.align || 'left',
                                          color:
                                            col.field === 'status' || col.field === 'severity'
                                              ? primaryColor
                                              : textColor,
                                        }}
                                      >
                                        {val}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                }

                return null;
              })}
            </div>

            {/* Footer Section */}
            {safeConfig.footer && (
              <div
                className="px-5 py-3 border-t text-[10.5px] leading-normal rich-text-preview"
                style={{
                  borderColor: borderColor,
                  color: mutedColor,
                  backgroundColor: 'rgba(128,128,128,0.05)',
                  textAlign: 'center',
                }}
                dangerouslySetInnerHTML={{
                  __html: safeConfig.footer,
                }}
              />
            )}
          </div>
        )}
      </div>

      <style>{`
        .rich-text-preview p {
          margin: 0 0 8px 0;
        }
        .rich-text-preview p:last-child {
          margin-bottom: 0;
        }
        .rich-text-preview strong {
          color: inherit;
          font-weight: 600;
        }
        .rich-text-preview code {
          background-color: rgba(128,128,128,0.12);
          color: #0284c7;
          padding: 2px 5px;
          border-radius: 4px;
          font-size: 11px;
        }
        .dark .rich-text-preview code {
          color: #38bdf8;
        }
        .rich-text-preview h1 {
          display: block;
          font-size: 1.5rem;
          font-weight: 700;
          color: inherit;
          margin: 12px 0 6px 0;
          line-height: 1.25;
        }
        .rich-text-preview h2 {
          display: block;
          font-size: 1.25rem;
          font-weight: 700;
          color: inherit;
          margin: 10px 0 4px 0;
          line-height: 1.3;
        }
        .rich-text-preview h3 {
          display: block;
          font-size: 1.1rem;
          font-weight: 600;
          color: inherit;
          margin: 8px 0 4px 0;
          line-height: 1.35;
        }
        .rich-text-preview ul {
          list-style-type: disc;
          padding-left: 24px;
          margin: 8px 0;
        }
        .rich-text-preview ol {
          list-style-type: decimal;
          padding-left: 24px;
          margin: 8px 0;
        }
        .rich-text-preview li {
          margin-bottom: 2px;
          display: list-item;
        }
      `}</style>
    </div>
  );
};
export default HtmlEmailPreview;
